import { existsSync, readFileSync } from "node:fs";
import { resolve, dirname } from "node:path";
import type { CaiConfig, ScaffoldSettings } from "./types.js";

const PRIMARY_SCAFFOLD_DIR = ".cai";
const LEGACY_SCAFFOLD_DIR = ".context-condensing";

const configCache = new Map<string, CaiConfig>();

/**
 * Resolve config by preferring the nearest scaffold root from startDir,
 * then falling back to git root. Result is memoized per resolved directory.
 */
export function findConfig(startDir?: string): CaiConfig {
  const dir = resolve(startDir ?? process.cwd());
  const cached = configCache.get(dir);
  if (cached) return cached;

  // Prefer nearest scaffold directory (supports nested projects inside a larger git repo)
  const nearestScaffoldProjectRoot = findNearestScaffoldProjectRoot(dir);
  const projectRoot = nearestScaffoldProjectRoot ?? findProjectRoot(dir) ?? dir;

  const scaffoldRoot = findScaffoldRoot(projectRoot);
  if (!scaffoldRoot) {
    throw new Error(buildScaffoldNotFoundMessage(dir, projectRoot));
  }

  const settings = loadSettings(projectRoot, scaffoldRoot);
  const subProjectRoot = settings.projectSubRoot
    ? resolve(projectRoot, settings.projectSubRoot)
    : projectRoot;

  const config: CaiConfig = {
    projectRoot,
    scaffoldRoot,
    subProjectRoot,
    settings,
  };
  configCache.set(dir, config);
  return config;
}

function buildScaffoldNotFoundMessage(startDir: string, projectRoot: string): string {
  // Case 1: user is inside an existing .cai/ directory
  if (startDir.includes(`/${PRIMARY_SCAFFOLD_DIR}/`) || startDir.includes(`/${LEGACY_SCAFFOLD_DIR}/`)) {
    return (
      `You are inside a scaffold directory, not a project root.\n\n` +
      `  Change to your project root and try again:\n\n` +
      `    cd ..\n` +
      `    cai check\n`
    );
  }

  // Case 2: no .git found at all — not a git repo
  const gitRoot = findProjectRoot(startDir);
  if (!gitRoot) {
    return (
      `No git repository found from ${startDir}.\n\n` +
      `  CAI works inside a git-tracked project.\n\n` +
      `  Initialize git first, then run setup:\n\n` +
      `    git init\n` +
      `    cai setup\n`
    );
  }

  // Case 3: git found, scaffold missing
  return (
    `No CAI scaffold found in ${projectRoot}.\n\n` +
    `  CAI needs to be set up for this project first.\n` +
    `  This creates a .cai/ directory with structured context about your codebase\n` +
    `  that AI agents use to understand your project.\n\n` +
    `  Run:  cai setup\n`
  );
}

function findNearestScaffoldProjectRoot(dir: string): string | null {
  let current = resolve(dir);
  while (true) {
    if (findScaffoldRoot(current)) {
      return current;
    }
    const parent = dirname(current);
    if (parent === current) return null;
    current = parent;
  }
}

function findProjectRoot(dir: string): string | null {
  let current = resolve(dir);
  while (true) {
    if (existsSync(resolve(current, ".git"))) {
      return current;
    }
    const parent = dirname(current);
    if (parent === current) return null;
    current = parent;
  }
}

function findScaffoldRoot(projectRoot: string): string | null {
  const primaryDir = resolve(projectRoot, PRIMARY_SCAFFOLD_DIR);
  if (existsSync(primaryDir)) return primaryDir;

  const legacyDir = resolve(projectRoot, LEGACY_SCAFFOLD_DIR);
  if (existsSync(legacyDir)) return legacyDir;

  // Fall back to context/ directory (repo-local scaffold layout)
  const contextDir = resolve(projectRoot, "context");
  if (existsSync(contextDir)) return projectRoot;

  return null;
}

function loadSettings(
  projectRoot: string,
  scaffoldRoot: string
): ScaffoldSettings {
  const candidates = [
    resolve(projectRoot, PRIMARY_SCAFFOLD_DIR, "config.json"),
    resolve(projectRoot, LEGACY_SCAFFOLD_DIR, "config.json"),
    resolve(projectRoot, "cai.config.json"),
    resolve(projectRoot, "context-condensing.config.json"),
  ];

  if (scaffoldRoot !== projectRoot) {
    candidates.unshift(resolve(scaffoldRoot, "config.json"));
  }

  for (const path of candidates) {
    if (!existsSync(path)) continue;
    try {
      const parsed = JSON.parse(readFileSync(path, "utf-8"));
      return typeof parsed === "object" && parsed !== null
        ? parsed as ScaffoldSettings
        : {};
    } catch (err) {
      process.stderr.write(`Warning: could not parse config at ${path}: ${(err as Error).message}\n`);
      continue;
    }
  }

  return {};
}
